<template>
  <div>
    <vue-easy-lightbox
      escDisabled
      moveDisabled
      :visible="visible"
      :imgs="images"
      :index="index"
      @hide="handleHide"
    ></vue-easy-lightbox>

    <div class="feed-wrapper feed-viewport-lock">
      <div class="feed-layout">
        <aside class="feed-sidebar">
          <div class="club-filter-section">
            <div class="club-filter-header">
              <span class="club-filter-title">
                <a-icon type="filter" />
                {{ $t("actualite.searchAndFilters") }}
              </span>
              <div class="club-filter-total">
                <span class="club-total-count">
                  <span class="club-total-badge">{{ totalItems }}</span>
                  {{ $t("activityClub.tabClub") }}
                </span>
              </div>
            </div>
            <a-input
              v-model="searchTitle"
              :placeholder="$t('activityClub.searchByTitle')"
              allow-clear
              @pressEnter="handleSearch"
              @change="onSearchClear"
            >
              <a-icon slot="prefix" type="search" />
            </a-input>
          </div>
        </aside>

        <main class="feed-main">
          <div class="feed-scroll">
          <div class="comments-toggle-section">
            <div class="comments-toggle-row">
              <span class="toggle-icon"><a-icon type="message" /></span>
              <span class="toggle-label">{{ $t("activityClub.enableComments") }}</span>
              <a-switch
                v-model="enableClubComments"
                @change="handleCommentsToggle"
                :checked="enableClubComments"
              />
            </div>
            <div class="toggle-hint">
              {{
                enableClubComments
                  ? $t("activityClub.commentsEnabled")
                  : $t("activityClub.commentsDisabled")
              }}
            </div>
          </div>

          <actuality-composer
            mode="club"
            :logo="logo"
            @add="openForm()"
          />

          <div class="feed-body">
            <template v-if="loading">
              <div v-for="n in 3" :key="n" class="post-card skeleton-card">
                <div class="sk-header">
                  <div class="sk-avatar sk-pulse"></div>
                  <div class="sk-header-meta">
                    <div class="sk-line sk-title sk-pulse"></div>
                    <div class="sk-line sk-date sk-pulse"></div>
                  </div>
                  <div class="sk-options sk-pulse"></div>
                </div>
                <div class="sk-tags">
                  <div class="sk-chip sk-pulse"></div>
                  <div class="sk-chip sk-pulse short"></div>
                </div>
                <div class="sk-content">
                  <div class="sk-line sk-pulse"></div>
                  <div class="sk-line sk-pulse"></div>
                  <div class="sk-line sk-pulse short"></div>
                </div>
                <div class="sk-media sk-pulse"></div>
                <div class="sk-stats">
                  <div class="sk-line sk-pulse tiny"></div>
                  <div class="sk-line sk-pulse tiny"></div>
                </div>
                <div class="sk-actions">
                  <div class="sk-action sk-pulse"></div>
                  <div class="sk-action sk-pulse"></div>
                </div>
              </div>
            </template>
            <template v-else>
              <div v-if="posts.length === 0" class="text-center empty-state">
                <a-empty :description="$t('activityClub.noPosts')" />
              </div>
              <transition-group name="post-fade" tag="div" class="posts-list" appear>
                <actuality-post-card
                  v-for="(item, index) in posts"
                  :key="item._id"
                  :item="item"
                  :index="index"
                  :logo="logo"
                  :school-name="schoolName"
                  :settings="settings"
                  :emojis="emojis"
                  resource-type="activity-club"
                  detail-path="/activity-club/"
                  :enable-validation="false"
                  :enable-seen="false"
                  @edit="openForm"
                  @delete="removePost"
                  @show-image="show"
                  @like="submitLike"
                  @unlike="submitUnlike"
                  @toggle-comment="toggleComment"
                  @submit-comment="submitComment"
                  @delete-comment="deleteComment"
                >
                  <template slot="extra-tags">
                    <span class="club-subscriber-chip pending">
                      {{ $t("activityClub.pending") }}: {{ item.pendingCount || 0 }}
                    </span>
                    <span class="club-subscriber-chip accepted">
                      {{ $t("activityClub.accepted") }}: {{ item.acceptedCount || 0 }}
                    </span>
                    <span class="club-subscriber-chip rejected">
                      {{ $t("activityClub.rejected") }}: {{ item.rejectedCount || 0 }}
                    </span>
                  </template>
                </actuality-post-card>
              </transition-group>
            </template>
          </div>
          <div class="feed-pagination" v-if="totalItems > 0">
            <a-pagination
              v-model="currentPage"
              :total="totalItems"
              :pageSize="pageSize"
              @change="handlePageChange"
            />
          </div>
          </div>
        </main>
      </div>
    </div>

    <activity-club-form
      ref="formRef"
      :studentList="studentList"
      :classes="classes"
      :teacherList="teacherList"
      :activitiesList="activitiesList"
      :enableClubComments="enableClubComments"
      @saved="loadPosts"
    />
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { mapState } from "vuex";
import { showApiError } from "@/utilities/showApiError";
import ActivityClubForm from "./ActivityClubForm.vue";
import ActualityComposer from "../components/ActualityComposer.vue";
import ActualityPostCard from "../components/ActualityPostCard.vue";
import { Modal } from "ant-design-vue";

export default {
  name: "ActivityClubIndex",
  components: { ActivityClubForm, ActualityComposer, ActualityPostCard },
  props: {
    studentList: { type: Array, default: () => [] },
    classes: { type: Array, default: () => [] },
    teacherList: { type: Array, default: () => [] },
    activitiesList: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      posts: [],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      searchTitle: "",
      enableClubComments: true,
      visible: false,
      images: [],
      index: 0,
      emojis: [],
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    activeBuildingImage() {
      const images = this.settings?.image;
      if (!Array.isArray(images) || !images.length) return null;
      return (
        images.find((img) => img.db === this.settings.activeBuilding) ||
        images[0] ||
        null
      );
    },
    logo() {
      return this.activeBuildingImage?.logo || null;
    },
    schoolName() {
      return this.activeBuildingImage?.name || "";
    },
  },
  created() {
    this.loadCommentsSetting();
    this.loadPosts();
    fetch("/emojis.json")
      .then((res) => res.json())
      .then((data) => (this.emojis = data))
      .catch(() => {});
  },
  methods: {
    loadCommentsSetting() {
      const building = this.user?.building?.filter(
        (el) => el.dbName == this.settings.activeBuilding
      )[0];
      if (building && building.enableClubComments !== undefined) {
        this.enableClubComments = building.enableClubComments;
      }
    },
    async handleCommentsToggle(checked) {
      try {
        await apiClient.patch("/activity-club/settings/comments", {
          enableClubComments: checked,
        });
        this.enableClubComments = checked;
        await this.loadPosts();
        this.$message.success(this.$t("success.changementEffectue"));
      } catch (error) {
        this.enableClubComments = !checked;
        showApiError(this, error);
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.scrollFeedToTop();
      this.loadPosts();
    },
    onSearchClear(e) {
      if (e && e.target && e.target.value === "") {
        this.currentPage = 1;
        this.loadPosts();
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.scrollFeedToTop();
      this.loadPosts().then(() => {
        this.$nextTick(() => this.scrollFeedToTop());
      });
    },
    scrollFeedToTop() {
      const main = this.$el.querySelector(".feed-scroll");
      if (main) main.scrollTop = 0;
    },
    async loadPosts() {
      this.loading = true;
      try {
        const { data } = await apiClient.get("/activity-club/find", {
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            title: this.searchTitle || undefined,
          },
        });
        this.posts = (data.data || []).map((item) => ({
          ...item,
          photos: [item.coverImage, ...(item.attachments || [])].filter(Boolean),
          processing: false,
          showComments: true,
        }));
        this.totalItems =
          (data.pagination && data.pagination.totalItems) ||
          this.posts.length ||
          0;
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.loading = false;
      }
    },
    openForm(item) {
      this.$refs.formRef.show(item);
    },
    removePost(id) {
      Modal.confirm({
        title: this.$t("tresorerie.supprimer"),
        content: this.$t("activityClub.confirmDeletePost"),
        okText: this.$t("tresorerie.oui"),
        cancelText: this.$t("tresorerie.non"),
        okType: "danger",
        onOk: async () => {
          try {
            await apiClient.put("/activity-club/" + id + "/remove");
            this.$message.success(this.$t("success.suppression"));
            this.loadPosts();
          } catch (err) {
            showApiError(this, err);
          }
        },
      });
    },
    toggleComment(index) {
      this.posts[index].showComments = !this.posts[index].showComments;
    },
    submitLike(postId, postIndex) {
      this.posts[postIndex].processing = true;
      this.posts[postIndex].liked = true;
      this.posts[postIndex].total_likes =
        (this.posts[postIndex].total_likes || 0) + 1;

      apiClient
        .post("/activity-club/" + postId + "/like", {
          type: "Admin",
          fullName: this.user?.userName,
        })
        .catch((e) => {
          this.posts[postIndex].liked = false;
          this.posts[postIndex].total_likes = Math.max(
            0,
            (this.posts[postIndex].total_likes || 1) - 1
          );
          showApiError(this, e);
        })
        .finally(() => {
          this.posts[postIndex].processing = false;
        });
    },
    submitUnlike(postId, postIndex) {
      this.posts[postIndex].processing = true;
      this.posts[postIndex].liked = false;
      this.posts[postIndex].total_likes = Math.max(
        0,
        (this.posts[postIndex].total_likes || 1) - 1
      );

      apiClient
        .delete("/activity-club/" + postId + "/like", { data: { type: "Admin" } })
        .catch((e) => {
          this.posts[postIndex].liked = true;
          this.posts[postIndex].total_likes =
            (this.posts[postIndex].total_likes || 0) + 1;
          showApiError(this, e);
        })
        .finally(() => {
          this.posts[postIndex].processing = false;
        });
    },
    async submitComment(comment, postId) {
      if (!comment || !comment.trim()) return;
      try {
        const { data } = await apiClient.post("/activity-club/" + postId + "/comment", {
          desc: comment,
          type: "Admin",
          fullName: this.user?.userName,
        });
        const postIndex = this.posts.findIndex((p) => p._id === postId);
        if (postIndex === -1) return;
        const created =
          (data && (data.entity || data.comment)) || {
            _id: Date.now().toString(),
            desc: comment,
            type: "Admin",
            fullName: this.user?.userName,
            createdAt: new Date().toISOString(),
          };
        if (!Array.isArray(this.posts[postIndex].comments)) {
          this.$set(this.posts[postIndex], "comments", []);
        }
        this.posts[postIndex].comments.push(created);
      } catch (e) {
        showApiError(this, e);
      }
    },
    async deleteComment(commentId, postIndex) {
      const post = this.posts[postIndex];
      if (!post) return;
      const previousComments = [...(post.comments || [])];
      post.comments = previousComments.filter((c) => c._id !== commentId);
      try {
        await apiClient.delete(
          "/activity-club/" + post._id + "/comment/" + commentId
        );
      } catch (e) {
        this.$set(this.posts[postIndex], "comments", previousComments);
        showApiError(this, e);
      }
    },
    isImageFile(file) {
      const ext = file.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    },
    show(files, fileIndex) {
      const currentFile = files[fileIndex];
      if (this.isImageFile(currentFile)) {
        const imageFiles = files.filter((f) => this.isImageFile(f));
        this.images = imageFiles.map((f) =>
          this.settings.resourcesUrl.replace("FILE_PATH", f)
        );
        this.index = imageFiles.indexOf(currentFile);
        this.visible = true;
      } else {
        window.open(
          this.settings.resourcesUrl.replace("FILE_PATH", currentFile),
          "_blank"
        );
      }
    },
    handleHide() {
      this.visible = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../components/feed-shared.scss";
</style>
